import {
  ApplicationRef,
  ComponentRef,
  createComponent,
  DestroyRef,
  DOCUMENT,
  EmbeddedViewRef,
  EnvironmentInjector,
  inject,
  Injectable,
  Injector,
  Type,
} from '@angular/core';
import { DialogTemplateComponent } from '@core/dialog/dialog-template/dialog-template.component';
import { BuilderCommunicationService } from '@website-builder/application/builder-communication.service';
import { DialogConfig } from '@core/dialog/shared/models/dialog-config';
import { BaseDialog } from '@core/dialog/shared/models/base-dialog.interface';
import { DialogRef } from '@core/dialog/shared/utilities/dialog-ref';

import { DialogInjector } from '@core/dialog/shared/utilities/dialog-injector';
import { filter, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationStart, Router } from '@angular/router';
import { DialogStackItem } from '@core/dialog/shared/models/dialog-stack-item.interface';

@Injectable({ providedIn: 'root' })
export class DialogService {
  readonly document = inject(DOCUMENT);
  readonly #router = inject(Router);
  readonly #injector = inject(Injector);
  readonly #destroyRef = inject(DestroyRef);
  readonly #appRef = inject(ApplicationRef);
  readonly #envInjector = inject(EnvironmentInjector);
  readonly #communicationService = inject(BuilderCommunicationService);

  #dialogStack: DialogStackItem[] = [];

  readonly #onEscKeydown = (keyboardEvent: KeyboardEvent): void => {
    if (keyboardEvent.key !== 'Escape') {
      return;
    }

    if (this.#dialogStack.length === 0) {
      return;
    }

    this.#topDialog?.instance.closeDialog();
  };

  constructor() {
    this.#router.events
      .pipe(
        filter((e): e is NavigationStart => e instanceof NavigationStart),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => this.closeDialogs(true));
  }

  open<T extends BaseDialog, R>(type: Type<T>, config?: DialogConfig): DialogRef<T, R> {
    const { componentRef, dialogRef } = this.#appendDialogComponentToBody<T, R>(config ?? ({} as DialogConfig));

    componentRef.changeDetectorRef.detectChanges();

    const childComponentRef = componentRef.instance.attachChildComponent(type);

    if (childComponentRef) {
      dialogRef.setComponentInstance(childComponentRef.instance);
      componentRef.changeDetectorRef.detectChanges();
    }

    return dialogRef;
  }

  closeDialogs(closeOnNavigationOnly = false): void {
    for (const dialog of [...this.#dialogStack]) {
      if (closeOnNavigationOnly && dialog.config.closeOnNavigation === false) {
        continue;
      }

      dialog.componentRef.instance.closeDialog();
    }
  }

  #ensureEscListener(): void {
    if (this.#dialogStack.length === 1) {
      this.document.addEventListener('keydown', this.#onEscKeydown);
    }
  }

  #cleanupEscListener(): void {
    if (this.#dialogStack.length === 0) {
      this.document.removeEventListener('keydown', this.#onEscKeydown);
    }
  }

  #appendDialogComponentToBody<T extends BaseDialog, R>(
    config: DialogConfig,
  ): {
    dialogRef: DialogRef<T, R>;
    componentRef: ComponentRef<DialogTemplateComponent>;
  } {
    const map = new WeakMap();
    map.set(DialogConfig, config);

    const dialogRef = new DialogRef<T, R>();
    map.set(DialogRef, dialogRef);

    const componentRef = createComponent(DialogTemplateComponent, {
      environmentInjector: this.#envInjector,
      elementInjector: new DialogInjector(this.#injector, map),
    });

    dialogRef
      .afterClosed$()
      .pipe(take(1))
      .subscribe(() => {
        this.#removeDialogComponentFromBody(componentRef, config);
      });

    this.#appRef.attachView(componentRef.hostView);

    const domElem = (componentRef.hostView as EmbeddedViewRef<unknown>).rootNodes[0] as HTMLElement;
    this.document.body.appendChild(domElem);

    if (!config?.overPanel) {
      this.#communicationService.toggleBodyScroll(true);
    }

    this.#dialogStack.push({ componentRef, config });
    this.#ensureEscListener();

    componentRef.setInput('dialogTitle', config?.title ?? '');
    componentRef.setInput('dialogStyle', config?.style ?? '');

    if (config?.data && typeof config.data == 'object' && 'background' in config.data && config.data['background']) {
      componentRef.setInput('dialogBackground', config.data['background']);
    }

    return { dialogRef, componentRef };
  }

  #removeDialogComponentFromBody(componentRef: ComponentRef<DialogTemplateComponent>, config: DialogConfig): void {
    const index = this.#dialogStack.findIndex(dialog => dialog.componentRef === componentRef);

    if (index === -1) {
      return;
    }

    const dialogStackItem = this.#dialogStack[index];

    dialogStackItem.componentRef.injector.get(DialogRef).setComponentInstance(null);
    componentRef.instance.animateDialog.set(false);

    this.#appRef.detachView(componentRef.hostView);
    componentRef.destroy();

    this.#dialogStack.splice(index, 1);

    this.#cleanupEscListener();

    if (this.#dialogStack.length === 0 && !config?.overPanel) {
      this.#communicationService.toggleBodyScroll(false);
    }
  }

  get #topDialog(): ComponentRef<DialogTemplateComponent> | null {
    return this.#dialogStack[this.#dialogStack.length - 1]?.componentRef ?? null;
  }
}
